import { useEffect, useMemo, useState } from 'react';
import { BatchCreate, RULES, TemplateCreate, jstDate, lockKeys, pickupsOf, stopNoteId, type BatchCreated, type DraftPlan, type Client, type Driver, type Place, type StopInput, type Template } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { Dialog, useToast } from '../components/ui';
import { useDirectory, useNames } from '../lib/directory';
import { PlaceDialog } from '../components/PlaceDialog';
import { useMe } from '../lib/me';
import { putNote, putTemplate, scheduleNoteId, showPlacesToDriver } from '../lib/fs';
import { RoutePlanBox } from '../components/RoutePlanBox';
import { LockBanner, useEditLock } from '../lib/locks';

type Stop = Omit<StopInput, 'from'> & { key: string; /** 届け先の荷物の積み場所（選んだときだけ） */ fromKeys?: string[]; /** 行き先ごとのメモ（Firestore の notes。ドライバーのその行き先の画面に出す） */ memo?: string };

/** 配送指示の入力（仕様書「配送指示の入力」）。回る順番は手で並べるか、おすすめの順にする。勤務中のドライバーは足したら何分増えるかの少ない順に勧める。メモは Firestore の notes に書く */
export function DispatchPage() {
  const toast = useToast();
  const me = useMe();
  const names = useNames();
  const dir = useDirectory();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [places, setPlaces] = useState<Place[]>([]);
  const [driverId, setDriverId] = useState<string>('');
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<string>('');
  const [stops, setStops] = useState<Stop[]>([]);
  const [memo, setMemo] = useState('');
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [sent, setSent] = useState<BatchCreated | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [saving, setSaving] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplErr, setTplErr] = useState('');

  useEffect(() => {
    api.get<{ drivers: Driver[] }>('/v1/office/drivers').then((r) => setDrivers(r.drivers.filter((d) => d.status !== 'retired')));
    loadPlaces();
    api.get<{ clients: Client[] }>('/v1/office/clients').then((r) => setClients(r.clients));
    loadTemplates();
  }, []);
  const loadPlaces = () => api.get<{ places: Place[] }>('/v1/office/places').then((r) => setPlaces(r.places)).catch(() => {});
  // 行き先の場所を探す（名前・住所・電話）。空のときはお気に入りだけ。急なスポットの依頼は「新しい場所」でその場で登録して足す
  const [q, setQ] = useState('');
  const [newPlace, setNewPlace] = useState<Stop['kind'] | null>(null);
  const loadTemplates = () => api.get<{ templates: Template[] }>('/v1/office/templates').then((r) => setTemplates(r.templates)).catch(() => {});
  // ひな形を選ぶと、行き先の欄に読み込む（仕様書「ひな形」）
  const loadTemplate = (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setStops(t.stops.map((s) => ({ key: crypto.randomUUID(), kind: s.kind, placeId: s.placeId, dueAt: s.dueAt ?? undefined, workMin: s.workMin ?? undefined })));
    setErr('');
  };
  const saveTemplate = async () => {
    setTplErr('');
    const name = tplName.trim();
    if (!name || name.length > RULES.nameMax) { setTplErr(`名前を ${RULES.nameMax} 文字までで入れてください`); return; }
    const r = TemplateCreate.safeParse({ stops: body().stops });
    if (!r.success) { setTplErr(r.error.issues[0]?.message ?? '行き先を確かめてください'); return; }
    try {
      const { id } = await api.post<{ id: string }>('/v1/office/templates', r.data);
      await putTemplate(id, { officeId: me!.officeId, name });
      setSaving(false); setTplName(''); loadTemplates(); toast(`ひな形「${name}」に保存しました`);
    } catch (e) { setTplErr(e instanceof HttpError ? e.message : '保存できませんでした'); }
  };
  const placeName = { get: (id: string) => names.place(id) };
  const sorted = useMemo(() => [...places].sort((a, z) => Number(z.isFavorite) - Number(a.isFavorite) || names.place(a.id).localeCompare(names.place(z.id), 'ja')), [places, names]);
  const hits = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return sorted.filter((p) => p.isFavorite);
    return sorted.filter((p) => { const d = dir.places.get(p.id); const hay = `${d?.name ?? ''} ${d?.address ?? ''} ${d?.phone ?? ''}`.toLowerCase(); return words.every((w) => hay.includes(w)); }).slice(0, 50);
  }, [q, sorted, dir]);
  const add = (placeId: string, kind: Stop['kind']) => { setStops((s) => [...s, { key: crypto.randomUUID(), kind, placeId }]); setErr(''); };
  const move = (i: number, d: -1 | 1) => setStops((s) => { const n = [...s]; const j = i + d; if (j < 0 || j >= n.length) return s; [n[i], n[j]] = [n[j]!, n[i]!]; return n; });
  const patch = (i: number, p: Partial<Stop>) => setStops((s) => s.map((x, k) => (k === i ? { ...x, ...p } : x)));

  /** 届け先より前のピックアップ先と、いま選ばれている積み場所 */
  const before = (i: number) => stops.slice(0, i).filter((s) => s.kind === 'pickup');
  const chosen = (i: number) => stops[i]!.fromKeys?.filter((k) => before(i).some((p) => p.key === k)) ?? (pickupsOf(stops).get(i) ?? []).map((k) => stops[k]!.key);

  const body = () => ({
    driverId: driverId || undefined,
    clientId: clientId || undefined,
    stops: stops.map(({ key: _k, fromKeys, memo: _m, ...s }) => ({
      ...s, dueAt: s.dueAt || undefined,
      ...(fromKeys ? { from: stops.flatMap((x, k) => (fromKeys.includes(x.key) ? [k] : [])) } : {}),
    })),
  });
  // 回る順番のおすすめとドライバーの候補（入力が変わったら少し待ってから計算し直す）
  const [plan, setPlan] = useState<DraftPlan | null>(null);
  const planKey = JSON.stringify(body().stops);
  useEffect(() => {
    setPlan(null);
    if (stops.length < 2 || !BatchCreate.safeParse(body()).success) return;
    const t = setTimeout(() => { api.post<DraftPlan>('/v1/office/route-plan', { stops: body().stops }).then(setPlan).catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [planKey]);
  // おすすめの順に並べ替える。並べ替えで積み場所の対応が変わらないよう、届け先ごとの積み場所を先に決めておく
  const applyPlan = () => {
    if (!plan) return;
    const fixed = stops.map((s, i) => (s.kind === 'drop' ? { ...s, fromKeys: chosen(i) } : s));
    setStops(plan.plan.recommended.order.map((i) => fixed[i]!));
    toast('おすすめの順に並べ替えました');
  };
  const check = () => {
    const r = BatchCreate.safeParse(body());
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return false; }
    return true;
  };
  // 先の日付・時刻に送る予約（仕様書「月間スケジュール」：先の日付のスポット便もここから予約できる）
  const [reserve, setReserve] = useState<{ date: string; time: string } | null>(null);
  // 送り先のドライバーのその日の予定を編集している間はロックする（仕様書「同時編集の防止」）
  const lockDate = reserve?.date || jstDate(new Date().toISOString());
  const lock = useEditLock(driverId ? lockKeys.driverDay(driverId, lockDate) : null);
  const submitReserve = async () => {
    if (!reserve) return;
    setConfirm(false);
    try {
      const b = body();
      if (b.stops.some((x) => 'from' in x)) { setErr('予約では「積む場所」の選び直しはまだ使えません。並び順で決まります'); return; }
      const { id } = await api.post<{ id: string }>('/v1/office/schedules', { date: reserve.date, sendTime: reserve.time, driverId: driverId || null, stops: b.stops });
      // 予約のメモは予定に付けておく（送るときに配送アプリが便のメモとして引く）
      if (me) {
        const to = driverId ? { driverId } : {};
        await Promise.all([
          memo.trim() ? putNote(scheduleNoteId(id), { officeId: me.officeId, kind: 'batch', text: memo.trim(), ...to }) : null,
          ...stops.map((x, i) => (x.memo?.trim() ? putNote(stopNoteId(scheduleNoteId(id), i), { officeId: me.officeId, kind: 'stop', text: x.memo.trim(), ...to }) : null)),
        ]).catch(() => toast('メモを保存できませんでした。予約はできています'));
      }
      if (driverId && me) await showPlacesToDriver(me.officeId, driverId, stops.map((x) => x.placeId)).catch(() => toast('行き先の情報をドライバーに渡せませんでした。予定から担当を選び直してください'));
      toast(`${reserve.date.slice(5).replace('-', '/')} ${reserve.time} に送る予約をしました。「予定」で見られます`);
      setStops([]); setMemo(''); setDriverId(''); setReserve(null);
    } catch (e) { setErr(e instanceof HttpError ? e.message : '予約できませんでした'); }
  };
  const submit = async () => {
    if (reserve) { await submitReserve(); return; }
    setConfirm(false);
    try {
      const r = await api.post<BatchCreated>('/v1/office/batches', body());
      // ドライバーのアプリに行き先の名前・住所が出るように印を付ける
      if (driverId && me) await showPlacesToDriver(me.officeId, driverId, stops.map((s) => s.placeId)).catch(() => toast('行き先の情報をドライバーに渡せませんでした。もう一度送り直してください'));
      if (memo.trim() && me) await putNote(r.batchId, { officeId: me.officeId, kind: 'batch', text: memo.trim(), ...(driverId ? { driverId } : {}) }).catch(() => toast('メモを保存できませんでした。配送は作れています'));
      // 行き先ごとのメモ（ID は入力した順。並べ替えても変わらない）
      if (me) await Promise.all(stops.map((x, i) => (x.memo?.trim() ? putNote(stopNoteId(r.batchId, i), { officeId: me.officeId, kind: 'stop', text: x.memo.trim(), ...(driverId ? { driverId } : {}) }) : null)))
        .catch(() => toast('行き先のメモを保存できませんでした。配送は作れています'));
      setSent(r); setStops([]); setMemo(''); setDriverId('');
      toast(r.status === 'sent' ? 'ドライバーに送信しました' : '未割り当てで保存しました');
    } catch (e) { setErr(e instanceof HttpError ? e.message : '送信できませんでした'); }
  };
  const drops = stops.filter((s) => s.kind === 'drop').length;

  return (
    <div className="page" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 16, alignItems: 'start' }}>
      <section className="panel" style={{ padding: 18, display: 'grid', gap: 14 }}>
        <div className="field"><label htmlFor="tpl">ひな形から読み込む</label>
          <select id="tpl" value="" onChange={(e) => loadTemplate(e.target.value)}>
            <option value="">{templates.length ? '選んでください' : 'ひな形はまだありません'}</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{names.template(t.id)}</option>)}
          </select></div>
        <div className="field"><label htmlFor="drv">ドライバー（選ばなければ未割り当てで保存）</label>
          <select id="drv" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
            <option value="">未割り当て</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{names.driver(d.id)}</option>)}
          </select>
          {plan && plan.candidates.length > 0 && (
            <div aria-label="おすすめのドライバー" style={{ display: 'grid', gap: 4, marginTop: 6 }}>
              <small className="muted">勤務中の人の今の予定に足したら（少ない順）</small>
              {plan.candidates.slice(0, 5).map((c) => (
                <button key={c.driverId} className="btn sm" style={{ justifyContent: 'space-between', display: 'flex', fontWeight: c.driverId === driverId ? 800 : 500 }} onClick={() => setDriverId(c.driverId)}>
                  <span>{names.driver(c.driverId)}</span>
                  <span>+{c.addMin} 分{c.lateMin ? <b className="err">・必着に {c.lateMin} 分遅れる</b> : ''}{c.overRestraint ? <b className="err">・拘束時間が上限に近い</b> : ''}</span>
                </button>
              ))}
            </div>
          )}</div>
        <div className="field"><label htmlFor="cli">荷主（社内の移動などは空のまま）</label>
          <select id="cli" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">なし</option>
            {clients.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div className="field"><label>行き先（回る順に並べる。届け先ごとに配送番号を発行・届け先は {RULES.maxDropsPerBatch} か所まで）</label>
          {stops.length === 0 ? <div className="empty">右の「行き先を選ぶ」から足してください（お気に入り・検索・新しい場所）</div> : stops.map((s, i) => (
            <div key={s.key} style={{ display: 'grid', gridTemplateColumns: '30px 1fr auto auto auto', gap: 8, alignItems: 'center', background: 'var(--surface-2)', borderRadius: 12, padding: '8px 10px' }}>
              <b style={{ width: 28, height: 28, borderRadius: s.kind === 'drop' ? '50%' : 6, background: s.kind === 'drop' ? 'var(--go)' : '#000', color: '#fff', display: 'grid', placeItems: 'center' }}>{s.kind === 'pickup' ? 'P' : 'D'}</b>
              <span><b>{placeName.get(s.placeId)}</b><small className="muted" style={{ display: 'block' }}>{s.kind === 'pickup' ? '積む' : '届ける'}</small></span>
              <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>作業<input aria-label="作業時間（分）" style={{ width: 56, padding: '4px 6px' }} inputMode="numeric" value={s.workMin ?? ''} placeholder="目安" onChange={(e) => patch(i, { workMin: e.target.value ? Number(e.target.value) : undefined })} />分</label>
              <input aria-label="必着時刻" style={{ width: 90, padding: '4px 6px' }} placeholder="必着 --:--" value={s.dueAt ?? ''} onChange={(e) => patch(i, { dueAt: e.target.value })} />
              <span style={{ display: 'flex', gap: 4 }}>
                <button className="btn sm" onClick={() => move(i, -1)} aria-label="上へ">↑</button>
                <button className="btn sm" onClick={() => move(i, 1)} aria-label="下へ">↓</button>
                <button className="btn sm" onClick={() => setStops((x) => x.filter((_, k) => k !== i))} aria-label="消す">✕</button>
              </span>
              <input aria-label={`${placeName.get(s.placeId)} へのメモ`} maxLength={RULES.visitMemoMax} value={s.memo ?? ''} onChange={(e) => patch(i, { memo: e.target.value })} placeholder={`この行き先へのメモ（任意・${RULES.visitMemoMax} 文字まで。例：箱 3 つのうち 1 つ）`} style={{ gridColumn: '2 / -1', padding: '4px 8px', fontSize: 13 }} />
              {s.kind === 'drop' && before(i).length > 1 && (
                <span style={{ gridColumn: '2 / -1', display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12 }} role="group" aria-label={`${placeName.get(s.placeId)} の荷物の積み場所`}>
                  <span className="muted">積む場所</span>
                  {before(i).map((p) => <label key={p.key}><input type="checkbox" style={{ width: 'auto' }} checked={chosen(i).includes(p.key)} onChange={(e) => patch(i, { fromKeys: e.target.checked ? [...chosen(i), p.key] : chosen(i).filter((k) => k !== p.key) })} /> {placeName.get(p.placeId)}</label>)}
                </span>
              )}
            </div>
          ))}
        </div>
        {plan && <RoutePlanBox plan={plan.plan} onApply={applyPlan} />}
        <div className="field"><label htmlFor="memo">事務所からのメモ（{RULES.memoMax} 文字まで）</label><input id="memo" maxLength={RULES.memoMax} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="例：午前中にお願いします" /></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} checked={!!reserve} onChange={(e) => setReserve(e.target.checked ? { date: '', time: '' } : null)} /> 日時を決めて予約する</label>
          {reserve && <>
            <input type="date" aria-label="送る日" value={reserve.date} onChange={(e) => setReserve({ ...reserve, date: e.target.value })} style={{ width: 160 }} />
            <input type="time" aria-label="送る時刻" value={reserve.time} onChange={(e) => setReserve({ ...reserve, time: e.target.value })} style={{ width: 120 }} />
            <small className="muted">この時刻に担当のアプリへ自動で届きます</small>
          </>}
        </div>
        <LockBanner lock={lock} what={`${names.driver(driverId)} の ${lockDate.slice(5).replace('-', '/')} の予定`} />
        {err && <p className="err" role="alert">{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" disabled={!stops.length} onClick={() => { setSaving(true); setTplErr(''); }}>ひな形に保存</button>
          <button className="btn pri" disabled={!lock.mine} onClick={() => { if (reserve && (!reserve.date || !reserve.time)) { setErr('送る日と時刻を入れてください'); return; } if (check()) setConfirm(true); }}>{reserve ? '予約する' : driverId ? 'ドライバーに送信' : '未割り当てで保存'}</button>
        </div>
        {sent && (
          <div className="panel" style={{ padding: 14, boxShadow: 'none', background: 'var(--surface-2)' }} aria-live="polite">
            <b>{sent.status === 'sent' ? '送信しました' : '保存しました'}</b>
            {sent.deliveries.map((d) => <div key={d.deliveryNo} className="num" style={{ fontSize: 18, fontWeight: 800 }}>{d.deliveryNo} <small className="muted" style={{ fontSize: 13, fontWeight: 500 }}>→ {placeName.get(d.dropPlaceId)}</small></div>)}
          </div>
        )}
      </section>
      <aside className="panel" style={{ padding: 16, display: 'grid', gap: 8 }}>
        <b>行き先を選ぶ</b>
        <input type="search" aria-label="場所を探す" value={q} onChange={(e) => setQ(e.target.value)} placeholder="名前・住所・電話で探す（登録した場所すべて）" />
        <small className="muted">{!q.trim() ? 'お気に入り' : dir.ready ? `${hits.length} 件${hits.length === 50 ? '（多いので先頭の 50 件）' : ''}` : ''}</small>
        {hits.map((p) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface-2)', borderRadius: 12, padding: '8px 10px' }}>
            <span style={{ flex: 1, minWidth: 0 }}>{names.place(p.id)}{p.isFavorite && q.trim() ? '（お気に入り）' : ''}<small className="muted" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dir.places.get(p.id)?.address ?? ''}</small></span>
            <button className="btn sm" onClick={() => add(p.id, 'pickup')}>積む</button>
            <button className="btn sm" onClick={() => add(p.id, 'drop')}>届ける</button>
          </div>
        ))}
        {hits.length === 0 && <div className="empty">{!dir.ready ? '読み込み中…' : q.trim() ? '見つかりません。下の「新しい場所」から登録できます' : 'お気に入りの場所がまだありません。上の欄で探すか、新しい場所を登録してください'}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
          <small className="muted" style={{ gridColumn: '1 / -1' }}>急な依頼で初めての場所は、ここで登録してそのまま足せます</small>
          <button className="btn sm" onClick={() => setNewPlace('pickup')}>＋ 新しい積み先</button>
          <button className="btn sm" onClick={() => setNewPlace('drop')}>＋ 新しい届け先</button>
        </div>
      </aside>
      {newPlace && (
        <PlaceDialog place={null} favorite={false} title={newPlace === 'pickup' ? '新しい積み先を登録して足す' : '新しい届け先を登録して足す'} submitLabel={newPlace === 'pickup' ? '登録して積む先に足す' : '登録して届け先に足す'}
          onClose={() => setNewPlace(null)} onDone={(id) => { add(id, newPlace); setNewPlace(null); void loadPlaces(); }} />
      )}
      {saving && (
        <Dialog title="ひな形に保存" onClose={() => setSaving(false)}>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>{stops.map((s) => placeName.get(s.placeId)).join(' → ')}</p>
          <div className="field"><label htmlFor="tpl-name">ひな形の名前（{RULES.nameMax} 文字まで）</label><input id="tpl-name" maxLength={RULES.nameMax} value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="例：写真ラボ便 朝" autoFocus /></div>
          {tplErr && <p className="err" role="alert">{tplErr}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={() => setSaving(false)}>やめる</button><button className="btn pri" onClick={saveTemplate}>保存する</button></div>
        </Dialog>
      )}
      {confirm && (
        <Dialog title={reserve ? `${reserve.date.slice(5).replace('-', '/')} ${reserve.time} に${driverId ? ` ${names.driver(driverId)} へ` : ''}送る予約をしますか？` : driverId ? `${names.driver(driverId)} に送信しますか？` : '未割り当てで保存しますか？'} onClose={() => setConfirm(false)}>
          <p style={{ margin: 0 }}>{stops.map((s) => placeName.get(s.placeId)).join(' → ')}</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>届け先 {drops} か所 → 配送番号を {drops} つ{reserve ? '、送るときに' : ''}発行します</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={() => setConfirm(false)}>戻る</button><button className="btn pri" onClick={submit}>{reserve ? '予約する' : driverId ? '送信する' : '保存する'}</button></div>
        </Dialog>
      )}
    </div>
  );
}
